import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { getFullName, getFullNameFa } from "@/utils/formatter";
import Image from "next/image";
import useGetUserMe from "@/hooks/useGetUserMe";
import IntroductoryMeetingModal from "@/components/IntroductoryMeetingModal";
import Button from "@/components/ui/button";
import persianJs from "persianjs";
import Price from "@/components/ui/price";
import Modal from "@/components/ui/modal";
import BookingModalContent from "@/components/BookingModalContent";
import { En, Fa } from "@/components/ui/multilang";
import Breadcrumb from "@/components/ui/Breadcrumb";
import Snack from "@/components/ui/Snack";
import RegisterNewStudents from "@/components/RegisterNewStudents";
import Space from "@/components/ui/Space";
import { DCategory, DCourse, DExpertise, DFeedback, DSiteProfile, DStudent, DTopic, DUser } from "@/types";
import Icon from "@/components/ui/Icon";
import ReviewsSection from "@/components/page-sections/tutor-profile/ReviewSection";
import { categories } from "@/constants/categories";
import LoginOrSignUpBox from "@/components/LoginOrSignUpBox";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { NextSeo } from "next-seo";
import { calculateTeacherRating } from "@/utils/calculateRating";
import supabaseServiceRole from "@/utils/supabaseServiceRole";
import { sendGTMEvent } from "@next/third-parties/google";
import AddPhoneForm from "@/components/AddPhoneForm";

type StudentQType = Pick<DStudent, "payerId" | "first_name" | "last_name">;
type ProfileDataType = DSiteProfile & { topic: Pick<DTopic, "name" | "nameFa"> } & {
  teacher: {
    expertise: Pick<DExpertise, "sessionPriceInCAD" | "sessionDurationOnWebsiteInMinute" | "startDate" | "endDate">;
  } & { user: Pick<DUser, "firstname" | "lastname" | "firstnameFa" | "lastnameFa"> };
} & { category: Pick<DCategory, "id" | "name" | "nameFa"> };
type ReviewQType = DFeedback & { course: Pick<DCourse, "teacherId"> } & {
  payer: { user: Pick<DUser, "firstname" | "lastname"> };
};
type CourseQType = Pick<DSiteProfile, "id" | "teacherId" | "topicId" | "categoryId"> & {
  teacher: {
    expertise: Pick<DExpertise, "sessionDurationOnWebsiteInMinute" | "startDate" | "endDate">;
  } & { user: Pick<DUser, "firstname" | "lastname" | "firstnameFa" | "lastnameFa"> };
} & { topic: Pick<DTopic, "name" | "nameFa"> };
type PrevCourseQType = Pick<
  DCourse,
  "id" | "requestedSessionDurationInMinute" | "numberOfStudents" | "payerId" | "teacherId"
> & {
  teacher: {
    expertise: Pick<
      DExpertise,
      "id" | "topicId" | "startDate" | "endDate" | "sessionPriceInCAD" | "sessionDurationOnWebsiteInMinute"
    >;
  } & { courseStudent: { student: Pick<DStudent, "id" | "first_name" | "last_name"> } };
};

export async function getServerSideProps(context) {
  const { locale } = context;
  const { id } = context.params;
  const parts = id && id.split("-");
  const profileId = parts[parts.length - 1];

  const { data, error }: any = await supabaseServiceRole
    .from("siteProfile")
    .select(
      `*, teacher(
      expertise(topicId, sessionDurationOnWebsiteInMinute, sessionPriceInCAD, startDate, endDate),
      user(firstname, lastname, firstnameFa, lastnameFa),
      course(feedback(studentRating)), englishFluency
      ),
      topic(name, nameFa),
      category (id , name , nameFa)
      `
    )
    .eq("id", profileId)
    .single();

  if (error || !data) {
    return {
      notFound: true,
    };
  }

  const { firstname, lastname } = data.teacher.user;

  if ((firstname.toLowerCase() + "-" + lastname.toLowerCase()).replaceAll(" ", "-") + "-" + profileId !== id) {
    return {
      redirect: {
        destination: `/tutors/${(firstname.toLowerCase() + "-" + lastname.toLowerCase()).replaceAll(
          " ",
          "-"
        )}-${profileId}`,
        permanent: false, // Redirect to the correct URL
      },
    };
  }

  const topicId = data.topicId;
  data.teacher.expertise = data.teacher.expertise.filter((expertise: any) => expertise.topicId === topicId);
  const profileData = data;

  const desiredCategory = categories.find((cat) => cat.id === profileData.category.id);

  return {
    props: {
      profileData,
      locale,
      title:
        locale === "fa"
          ? `${getFullNameFa(profileData.teacher.user)} | درسون`
          : `${getFullName(profileData.teacher.user)} | Darsoon`,
      description:
        locale === "fa"
          ? `آشنایی بیشتر با ${getFullNameFa(profileData.teacher.user)} و سوابق کاری او در زمینه ${
              desiredCategory!.nameFa
            }. مشاهده نظرات شاگردان و ثبت درخواست جلسه معرفی`
          : `Exploring ${getFullName(profileData.teacher.user)}'s background and exprience in ${
              desiredCategory!.name
            }. Reviewing student testimonials and requesting an introductory meeting.`,
    },
  };
}

type Props = {
  profileData: ProfileDataType;
  title: string;
  description: string;
  locale: string;
};

export default function ProfilePage({ profileData, title, description, locale }: Props) {
  const { asPath } = useRouter();

  const baseUrl = "https://darsoon.com";
  const canonicalUrl = `${baseUrl}${locale === "fa" ? "" : "/en"}${asPath}`;

  const userMeQ = useGetUserMe();

  const studentQ = useQuery<StudentQType[]>({
    queryKey: ["studentChecker"],
    queryFn: async () => {
      const { data, error }: any = await supabase
        .from("student")
        .select(`payerId, first_name, last_name`)
        .eq("payerId", userMeQ.data?.payer.id);
      if (error) throw error;
      return data;
    },
    enabled: userMeQ.isSuccess && !!userMeQ.data?.isPayer,
  });

  const supabase = useSupabaseClient();

  const reviewQ = useQuery<ReviewQType[]>({
    queryKey: ["reviewNewWebsite", profileData.id],
    queryFn: async () => {
      const { data, error }: any = await supabase
        .from("feedback")
        .select(`*, course!inner(teacherId), payer(user(firstname, lastname))`)
        .filter("course.teacherId", "eq", profileData.teacherId)
        .not("studentFeedback", "eq", "");
      if (error) throw error;
      return data;
    },
  });

  const courseQ = useQuery<CourseQType>({
    queryKey: ["bookingModalRequest-1", profileData.id],
    queryFn: async () => {
      const { data, error }: any = await supabase
        .from("siteProfile")
        .select(
          `id, teacherId, topicId, teacher(expertise(sessionDurationOnWebsiteInMinute, startDate, endDate),user(firstname, lastname, firstnameFa, lastnameFa)), topic(name, nameFa), categoryId`
        )
        .eq("id", profileData.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!userMeQ.data?.payer?.id,
  });

  const prevCourseQ = useQuery<PrevCourseQType[]>({
    queryKey: ["bookingPrevCourses-2", profileData.id],
    queryFn: async () => {
      const { data, error }: any = await supabase
        .from("course")
        .select(
          `id, isExpired, requestedSessionDurationInMinute, numberOfStudents, payerId, teacherId, teacher(expertise(id, topicId, startDate, endDate, sessionPriceInCAD, sessionDurationOnWebsiteInMinute)), courseStudent(student(id, first_name, last_name)), session(packagePurchasedId, isHeld)`
        )
        .eq("payerId", userMeQ.data?.payer.id)
        .eq("teacherId", profileData.teacherId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!userMeQ.data?.payer?.id,
  });

  const breadcrumbSteps =
    locale === "fa"
      ? [
          { title: "خانه", href: "/" },
          { title: "کلاس‌ها", href: "/subjects" },
          {
            title: profileData.category.nameFa ?? "دسته‌بندی",
            href: `/${profileData.category.name.toLowerCase().replaceAll(" ", "-")}`,
          },
          {
            title: getFullNameFa(profileData.teacher.user) ?? "معلم",
            href: `/tutors/${getFullName(profileData.teacher.user).toLowerCase().replaceAll(" ", "-")}-${
              profileData.id
            }`,
          },
        ]
      : [
          { title: "Home", href: "/" },
          { title: "Subjects", href: "/subjects" },
          {
            title: profileData.category.name ?? "Category",
            href: `/${profileData.category.name.toLowerCase().replaceAll(" ", "-")}`,
          },
          {
            title: getFullName(profileData.teacher.user) ?? "Tutor",
            href: `/tutors/${getFullName(profileData.teacher.user).toLowerCase().replaceAll(" ", "-")}-${
              profileData.id
            }`,
          },
        ];

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        openGraph={{
          title: title,
          description: description,
          locale: locale === "fa" ? "fa_IR" : "en_US",
          site_name: locale === "fa" ? "درسون" : "Darsoon",
          url: canonicalUrl,
        }}
        additionalLinkTags={[
          {
            rel: "alternate",
            href: `${baseUrl}${asPath}`,
            hrefLang: "fa",
          },
          {
            rel: "alternate",
            href: `${baseUrl}/en${asPath}`,
            hrefLang: "en",
          },
          {
            rel: "alternate",
            href: `${baseUrl}${asPath}`,
            hrefLang: "x-default",
          },
        ]}
      />
      <div className="max-w-50rem mx-auto ">
        <Breadcrumb steps={breadcrumbSteps} />
        <div className="h-4"></div>
        <div className="  gap-8 w-full max-w-full    ">
          <HeaderSection profileData={profileData} />
          <ClassInformationCard
            profileData={profileData}
            userMeQ={userMeQ}
            studentQ={studentQ}
            reviewQ={reviewQ}
            courseQ={courseQ}
            prevCourseQ={prevCourseQ}
          />
          <div className="">
            <AboutSection profileData={profileData} />
          </div>
          <ReviewsSection reviewQ={reviewQ} prevCourseQ={prevCourseQ} />
        </div>
      </div>
    </>
  );
}

function HeaderSection({ profileData }) {
  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().split("T")[0];

  const supabase = useSupabaseClient();
  const { locale } = useRouter();

  const englishFluencyFa = {
    beginner: "کم",
    intermediate: "متوسط",
    advanced: "خوب",
    fluent: "عالی",
  };

  return (
    <section className="" style={{ gridColumn: 1 / 2, gridRow: 1 / 2 }}>
      <div className="flex gap-4  sm:gap-6  w-full   ">
        <div className="  flex">
          <Image
            src={`${supabase.storage.from("teacher").getPublicUrl(`photo/${profileData.id}`).data.publicUrl}`}
            alt={getFullName(profileData.teacher.user)}
            width={450}
            height={450}
            className="rd-4 sm:rd-6 w-40 h-40 sm:w-60 sm:h-60 block bg-sand4 shd-tinted-2 object-cover b-2 b-white"
          />
        </div>
        <div className="grow">
          <Space size="h-2 sm:h-4" />
          <h1 className="H1 !fw-700 text-3xl sm:text-5xl">
            <Fa>{getFullNameFa(profileData.teacher.user)}</Fa>
            <En>{getFullName(profileData.teacher.user)}</En>
          </h1>
          <div className="flex gap-2 mt-3">
            {calculateTeacherRating(profileData).rating !== -1 && (
              <>
                <Icon name="bf-i-ph-star-fill c-brand-orange" subdued={false} />
                <span>
                  <En>
                    {calculateTeacherRating(profileData).rating.toFixed(2)} |{" "}
                    {calculateTeacherRating(profileData).numberOfFeedbacks} ratings
                  </En>
                  <Fa>
                    {persianJs(calculateTeacherRating(profileData).rating.toFixed(2).toString())
                      .englishNumber()
                      .toString()}{" "}
                    از{" "}
                    {persianJs(calculateTeacherRating(profileData).numberOfFeedbacks.toString())
                      .englishNumber()
                      .toString()}{" "}
                    نظر
                  </Fa>
                </span>
              </>
            )}
          </div>
          <Space size="sm:h-4" />
          <div className="sm:text-lg grid sm:gap-0.5">
            <p className=" c-sand11 flex items-baseline">
              <Icon name="bf-i-ph-chalkboard-simple" className="mie-2 sm:mie-3" />
              <Fa>
                <span className="leading-6  ">{profileData.topic.nameFa}</span>
              </Fa>
              <En>
                <span className="leading-4 ">{profileData.topic.name}</span>
              </En>
            </p>

            <p className="flex items-baseline">
              <Icon name="bf-i-ph-timer" className="mie-2 sm:mie-3" />

              <span className="c-sand11">
                {locale === "fa" ? "مدت‌زمان هر جلسه" : "Duration"}:{" "}
                <Fa>
                  {profileData.teacher.expertise.find(
                    (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
                  )?.sessionDurationOnWebsiteInMinute &&
                    persianJs(
                      profileData.teacher.expertise
                        .find((exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate))
                        .sessionDurationOnWebsiteInMinute.toString()
                    )
                      .englishNumber()
                      .toString()}
                </Fa>
                <En>
                  {
                    profileData.teacher.expertise.find(
                      (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
                    )?.sessionDurationOnWebsiteInMinute
                  }
                </En>{" "}
                {locale === "fa" ? "دقیقه" : "Min"}
              </span>
            </p>
            <p className="flex items-baseline">
              <Icon name="bf-i-ph-currency-dollar-simple" className="mie-2 sm:mie-3" />

              <span className="c-sand11">
                {locale === "fa" ? "قیمت" : "Price"}:{" "}
                <Price>
                  <Fa>
                    {profileData.teacher.expertise.find(
                      (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
                    )?.sessionPriceInCAD &&
                      persianJs(
                        profileData.teacher.expertise
                          .find(
                            (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
                          )
                          .sessionPriceInCAD.toString()
                      )
                        .englishNumber()
                        .toString()}
                  </Fa>
                  <En>
                    {
                      profileData.teacher.expertise.find(
                        (exp) => exp.startDate <= todayDate && (exp.endDate === null || exp.endDate > todayDate)
                      )?.sessionPriceInCAD
                    }
                  </En>
                </Price>
              </span>
            </p>
            <p className="flex items-baseline">
              <Icon name="bf-i-ph-baby" className="mie-2 sm:mie-3" />
              <span className="c-sand11">
                {locale === "fa" ? "گروه سنی" : "Age Group"}:{` `}
                <Fa>{profileData.studentRangeFa.join("، ")}</Fa>
                <En>{profileData.studentRange.join(", ")}</En>
              </span>
            </p>
            <p className="flex items-baseline">
              <Icon name="bf-i-ph-translate" className="mie-2 sm:mie-3" />
              <span className="c-sand11">
                {locale === "fa" ? "میزان تسلط به زبان انگلیسی" : "English Fluency"}:{` `}
                <Fa>{englishFluencyFa[profileData.teacher.englishFluency.toLowerCase()]}</Fa>
                <En>{profileData.teacher.englishFluency}</En>
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Video({ profileId }) {
  const supabase = useSupabaseClient();
  const [videoExists, setVideoExists] = useState(false);

  useEffect(() => {
    const checkVideoExists = async () => {
      const videoUrl = supabase.storage.from("teacher").getPublicUrl(`video/${profileId}`).data.publicUrl;

      try {
        const response = await fetch(videoUrl, { method: "HEAD" });
        if (response.ok) {
          setVideoExists(true);
        } else {
          setVideoExists(false);
        }
      } catch (error) {
        setVideoExists(false);
      }
    };

    if (profileId) {
      checkVideoExists();
    }
  }, [profileId, supabase]);

  if (!videoExists) {
    return null;
  }

  return (
    <video width="640" height="480" controls className="rd-xl shadow-xl bg-sand7 w-full max-w-full aspect-16/9">
      <source
        src={`${supabase.storage.from("teacher").getPublicUrl(`video/${profileId}`).data.publicUrl}`}
        type="video/mp4"
      />
      Your browser does not support the video tag.
    </video>
  );
}

function ClassInformationCard({ profileData, userMeQ, studentQ, reviewQ, courseQ, prevCourseQ }) {
  const queryClient = useQueryClient();
  const registerPayerM = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/register-as-payer", {
        method: "POST",
      });
      const { data, error } = await res.json();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userMe"] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
  const { locale } = useRouter();

  const userPhoneQ = useQuery({
    queryKey: ["userPhoneIntroMeeting-1"],
    queryFn: async () => {
      const res = await fetch("/api/getSensitiveInfo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userMeQ.data.id,
          column: "phone",
        }),
      });

      const { data, error } = await res.json();
      if (error) throw new Error(error);
      return data;
    },
    enabled: !!userMeQ.data && !!userMeQ.data.id,
  });

  const [isIntroModalOpen, setIsIntroModalOpen] = useState<boolean>(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const [disabledClosing, setDisabledClosing] = useState<boolean>(false);

  return (
    <div className="" style={{ gridRow: " span 2" }}>
      <div className="">
        <div className="flex flex-col gap-1  ">
          <div className="h-4"></div>
          <Fa>
            {profileData.availabilityFa && (
              <>
                <Snack variant="warning">{profileData.availabilityFa}</Snack>
                <div className="h-1"></div>
              </>
            )}
          </Fa>
          <En>
            {profileData.availability && (
              <>
                <Snack variant="warning">{profileData.availability}</Snack>
                <div className="h-1"></div>
              </>
            )}
          </En>

          {profileData.isTeacherIn && (
            <>
              <div className="h-2"></div>

              <div className="flex justify-center flex-wrap  gap-4  rd-6   w-full">
                <Modal
                  isOpen={isIntroModalOpen}
                  setOpen={(v: boolean) => setIsIntroModalOpen(v)}
                  trigger={
                    <Button
                      variation="solid-prm"
                      disabled={!profileData.isActive}
                      className="text-lg flex-1 min-w-fit py-4 intro-request-pdp-button"
                      onClick={() => sendGTMEvent({ event: "intro-request-pdp-click" })}
                    >
                      <span
                        className="w-full inline-grid gap-1"
                        style={{
                          gridTemplateColumns: "1em 1fr 1em",
                        }}
                      >
                        <Icon name="bf-i-ph-chalkboard-teacher before:opacity-100 mie-2" />
                        <span className="text-center ">
                          {locale === "fa" ? "درخواست جلسه‌ی معرفی رایگان" : "Request a Free Introductory Meeting"}
                        </span>
                      </span>
                    </Button>
                  }
                >
                  {userMeQ.isLoading && <LoadingSpinner wrapped />}
                  {userMeQ.isFetching && registerPayerM.isSuccess && <LoadingSpinner wrapped />}
                  {!userMeQ.data && <LoginOrSignUpBox />}
                  {userMeQ.data && !userMeQ.data?.isPayer && !registerPayerM.isSuccess && (
                    <div>
                      <En>
                        <p>Please create a payer profile to be able to book classes.</p>
                      </En>
                      <Fa>
                        <p>Please create a payer profile to be able to book classes.</p>
                      </Fa>
                      <Button
                        variation="solid-prm"
                        onClick={() => {
                          registerPayerM.mutate();
                        }}
                        isLoading={registerPayerM.isLoading}
                      >
                        <En>Create a payer profile</En>
                        <Fa>Create a payer profile</Fa>
                      </Button>
                    </div>
                  )}
                  {userMeQ.data && userMeQ.data?.isPayer && !userPhoneQ.data?.phone && (
                    <AddPhoneForm userId={userMeQ.data.id} />
                  )}
                  {userMeQ.data?.isPayer && studentQ.data && userPhoneQ.data?.phone && studentQ.data.length === 0 && (
                    <RegisterNewStudents />
                  )}
                  {userMeQ.data?.isPayer && studentQ.data && userPhoneQ.data?.phone && studentQ.data.length > 0 && (
                    <IntroductoryMeetingModal courseQ={courseQ} profileId={profileData.id} prevCourseQ={prevCourseQ} />
                  )}
                </Modal>
                <Button
                  variation="soft-prm"
                  disabled={!profileData.isBuyButtonActive}
                  className="text-lg flex-1 min-w-fit py-4 pay-pdp-button"
                  onClick={() => {
                    setIsBookingModalOpen(!isBookingModalOpen);
                    sendGTMEvent({ event: "pay-pdp-click" });
                  }}
                >
                  <span
                    className="w-full inline-grid gap-1 "
                    style={{
                      gridTemplateColumns: "1em 1fr 1em",
                    }}
                  >
                    <Icon name="bf-i-ph-calendar mie-2" />
                    <span className="text-center ">{locale === "fa" ? "رزرو و پرداخت" : "Booking and Payment"}</span>
                  </span>
                </Button>
                <Modal
                  isOpen={isBookingModalOpen}
                  setOpen={() =>
                    setIsBookingModalOpen((prev) => {
                      if (disabledClosing) return true;
                      return !prev;
                    })
                  }
                  disabledClosing={disabledClosing}
                >
                  {userMeQ.isFetching && <LoadingSpinner wrapped />}
                  {!userMeQ.data && <LoginOrSignUpBox />}
                  {userMeQ.data && !userMeQ.data?.isPayer && (
                    <p>You Should be a payer to buy this class. Contact Admins Please.</p>
                  )}
                  {userMeQ.data?.isPayer && studentQ.data && studentQ.data.length === 0 && <RegisterNewStudents />}
                  {userMeQ.data?.isPayer && studentQ.data && studentQ.data.length > 0 && (
                    <BookingModalContent
                      courseQ={courseQ}
                      prevCourseQ={prevCourseQ}
                      setIsIntroModalOpen={setIsIntroModalOpen}
                      isActive={profileData.isActive}
                      setDisabledClosing={setDisabledClosing}
                      profileId={profileData.id}
                    />
                  )}
                </Modal>
              </div>
            </>
          )}
        </div>
        <div className="h-12"></div>
      </div>
    </div>
  );
}

function AboutSection({ profileData }) {
  const { locale } = useRouter();

  return (
    <div className="">
      <div className=""></div>
      <section className="">
        <div className="max-w-50rem mx-auto  ">
          <Video profileId={profileData.id} />
          <div className="h-12 "></div>
          <h2 className="H3 !fw-700 c-sand11">{locale === "fa" ? "درباره‌ی من" : "About Me"}</h2>
          <div className="h-1"></div>
          <p className="whitespace-pre-wrap">
            <Fa>{profileData.aboutFa}</Fa>
            <En>{profileData.about}</En>
          </p>
          <div className="h-12 "></div>
          {profileData.teachingMethod && (
            <>
              <h2 className="H3 !fw-700 c-sand11">{locale === "fa" ? "شیوه‌ی تدریس" : "Teaching Method"}</h2>
              <div className="h-1"></div>
              <p className="whitespace-pre-wrap">
                <Fa>{profileData.teachingMethodFa}</Fa>
                <En>{profileData.teachingMethod}</En>
              </p>
            </>
          )}
          <div className="h-12 "></div>
          {profileData.publicExperience && (
            <>
              <h2 className="H3 !fw-700 c-sand11">{locale === "fa" ? "تجربه‌ی تحصیلی و کاری" : "Experience"}</h2>
              <div className="h-1"></div>
              <p className="whitespace-pre-wrap">
                <Fa>{profileData.publicExperienceFa}</Fa>
                <En>{profileData.publicExperience}</En>
              </p>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
